// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import {
  ItemTriage,
  ItemTriageFilter,
  ItemTriageStatus,
  itemTriageSchema,
} from '@/items/api/triage-types';
import { z } from 'zod';

/**
 * Default page size for triage items listing.
 */
const DEFAULT_PAGE_SIZE = 50;

/**
 * Type definition for the API response wrapper.
 */
interface ApiResponse {
  data: {
    results: unknown[];
    totalCount: number;
  };
  metadata: { requestId: string };
}

/**
 * Type definition for the get triage items API response.
 */
interface TriageItemsResponse {
  items: ItemTriage[];
  totalCount: number;
}

/**
 * Fetches triage items with the specified filter criteria.
 *
 * @param filter - Optional filter parameters including pagination
 * @returns Promise containing the list of triage items and total count
 */
/**
 * Converts ItemTriageStatus to API status parameter.
 * 'all' -> undefined (no filter), otherwise pass through uppercase value.
 */
function toApiStatus(status?: ItemTriageStatus): string | undefined {
  if (!status || status === 'all') {
    return undefined;
  }
  return status;
}

export async function getTriageItems(
  filter?: ItemTriageFilter
): Promise<TriageItemsResponse> {
  const url = '/v1/item-triage';
  const response = await api.get<ApiResponse>(url, {
    params: {
      status: toApiStatus(filter?.status),
      page: filter?.page ?? 1,
      limit: filter?.limit ?? DEFAULT_PAGE_SIZE,
    },
  });

  // Parse and validate each triage item with Zod
  const triageItemsSchema = z.array(itemTriageSchema);
  const items = await parseResponse(
    triageItemsSchema,
    response.data.data.results,
    {
      schemaName: 'TriageItems',
      endpoint: url,
    }
  );

  return { items, totalCount: response.data.data.totalCount };
}
